import { Box, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useTranslation } from "react-i18next";

export interface StarRatingProps {
    averageRating: number;
    ratingCount: number;
    userRating?: number;
    emptySummaryLabel?: string;

    /**
     * Callback for when the user selects a star. 
     * If not provided, the stars won't be interactive.
     */
    onRatingChange?: (newRating: number) => void;
}

export function StarRating(props: StarRatingProps) {
    const { averageRating, ratingCount, userRating, onRatingChange, emptySummaryLabel } = props;
    const MAX_STARS = 5;
    const { t } = useTranslation();
    const isEmpty = ratingCount === 0;

    /**
     * Render the star icons for the average rating (Case 1).
     * If you want to support half-stars, you'd do some additional logic here.
     */
    const renderAverageStars = () => {
        // Round average to integer for star fill or do partial-star logic if needed
        const roundedAvg = Math.round(averageRating);
        const stars = [];

        for (let i = 1; i <= MAX_STARS; i++) {
            const isFilled = i <= roundedAvg;
            stars.push(
                <Box
                    key={`avg-star-${i}`}
                    component="span"
                    sx={{ color: isFilled ? "#FFD700" : "#ccc", px: 0.25 }}
                >
                    <StarIcon fontSize="medium" />
                </Box>
            );
        }
        return stars;
    };

    /**
     * Render the star icons for the user rating (Case 1 or Case 2).
     * If userRating is undefined, we'll show empty stars (prompt to rate).
     */
    const renderUserStars = () => {
        const stars = [];
        for (let i = 1; i <= MAX_STARS; i++) {
            const isFilled = userRating !== undefined && i <= userRating;
            stars.push(
                <Box
                    key={`user-star-${i}`}
                    component="span"
                    sx={{
                        cursor: onRatingChange ? "pointer" : "default",
                        color: isFilled ? "#FFA726" : "#ccc",
                        px: 0.4,
                        py: 0.2,
                    }}
                    onClick={() => {
                        if (onRatingChange) {
                            onRatingChange(i);
                        }
                    }}
                >
                    {isFilled ? <StarIcon fontSize="medium" /> : <StarBorderIcon fontSize="medium" />}
                </Box>
            );
        }
        return stars;
    };

    return (
        <Box>
            {/* Always show the average rating */}
            <Box display="flex" alignItems="center" mb={1}>
                {renderAverageStars()}
                <Typography variant="body2" ml={1}>
                    {isEmpty
                        ? (emptySummaryLabel || t('meal.rating_empty_cta'))
                        : t('meal.rating_summary', { average: averageRating.toFixed(1), count: ratingCount })}
                </Typography>
            </Box>

            {/* Distinguish between whether the user has rated or not */}
            {userRating !== undefined ? (
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" mr={1}>
                        {t('meal.rating_user_label')}
                    </Typography>
                    {renderUserStars()}
                </Box>
            ) : (
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" mr={1}>
                        {t('meal.rating_prompt')}
                    </Typography>
                    {renderUserStars()}
                </Box>
            )}
        </Box>
    );
};
